import { computed, nextTick, reactive, ref, shallowRef, toRaw } from 'vue'
import { isClient } from '@lit-element/utils'
import { nextFrameOnce } from './performance'
import { randomHardColor } from './color'
import { heartPath, puzzlePath, shieldPath, squarePath } from './hollow-paths'

export default function useCaptchaImg({
  failFn,
  successFn,
  slider,
  isSuccess,
  props = {},
  currentTarget,
}: any) {
  const pointers = reactive<number[][]>([])
  const testLoading = ref(false)
  const canvas = ref<HTMLCanvasElement>()
  const subCanvas = ref<HTMLCanvasElement>()
  const imageLoading = ref(false)
  const imagePromise = shallowRef(Promise.resolve())

  const pointerTargets: number[][] = []
  const fontRate = 0.108
  let imageLoaded = false
  let imgEle: HTMLImageElement | undefined
  let memoryCanvas: HTMLCanvasElement | undefined

  const track = computed(() => slider.value?.track)
  const isLoading = computed(() => {
    return props.loading || imageLoading.value || testLoading.value
  })

  const subCanvasStyle = computed(() => {
    return {
      left: `${slider.value?.currentLeft || 0}%`,
      transition: slider.value?.resetting ? 'left 250ms ease' : undefined,
    }
  })

  const actionLocked = computed(
    () => props.disabled || isSuccess.value || isLoading.value
  )

  // 避免多次触发时发生竞态问题
  let loadFlag: string

  async function loadImage() {
    if (imgEle) return

    imageLoading.value = true
    loadFlag = `${Date.now()}${Math.round(Math.random() * 10e6)}`

    const flag = loadFlag
    const src =
      typeof props.image === 'function' ? await props.image() : props.image

    await new Promise<void>((resolve) => {
      if (!isClient || flag !== loadFlag || !src) {
        resolve()
        return
      }

      imgEle = new Image()
      imageLoaded = false
      // imgEle.alt = '加载失败'
      imgEle.src = src

      if (src.trim().startsWith('data:image')) {
        imageLoaded = true
        resolve()
      } else {
        imgEle.onload = () => {
          imageLoaded = true
          resolve()
        }
        imgEle.onabort = () => {
          imageLoaded = true
          resolve()
        }
        imgEle.onerror = () => {
          imageLoaded = true
          resolve()
        }
      }
    }).finally(() => {
      imageLoading.value = false
    })
  }

  async function updateImgPromise(clear = false) {
    if (clear) {
      imgEle = undefined
    }
    await (imagePromise.value = loadImage())
  }

  function drawImageWithTexts() {
    const canvasEl = canvas.value
    const ctx = canvasEl?.getContext?.('2d')

    if (!imgEle || !canvasEl || !ctx) return

    const { width, height } = canvasEl

    ctx.drawImage(imgEle, 0, 0, width, height)

    if (!props.texts.length || props.remotePoint) return

    pointerTargets.length = 0

    const fontSize = Math.max(width, height) * fontRate

    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'
    ctx.font = `bold ${fontSize}px sans-serif`
    ctx.lineWidth = 2
    ctx.strokeStyle = '#fff'

    const drawText = (
      text: string,
      x: number,
      y: number,
      radian = 0,
      color = randomHardColor()
    ) => {
      ctx.save()
      ctx.translate(x, y)
      radian && ctx.rotate(radian * Math.PI)
      ctx.fillStyle = color
      ctx.fillText(text, 0, 0)
      ctx.strokeText(text, 0, 0)
      ctx.restore()
    }

    const metrics = ctx.measureText(String(props.texts[0]))
    const xLimit = Math.max(fontSize, metrics.width) * 1.2
    const yLimit =
      Math.max(
        fontSize,
        metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent
      ) * 1.2

    let prevX = -2 * fontSize
    let prevY = -2 * fontSize

    for (const text of props.texts) {
      let x = prevX
      let y = prevY

      while (Math.abs(x - prevX) < xLimit && Math.abs(y - prevY) < yLimit) {
        x = width * 0.1 + Math.random() * width * 0.8
        y = height * 0.1 + Math.random() * height * 0.8
      }

      prevX = x
      prevY = y

      pointerTargets.push([(x / width) * 100, (y / height) * 100])
      drawText(String(text), x, y, Math.random() * 2)
    }
  }

  function getHollowProcess() {
    if (typeof props.hollowShape === 'function') return props.hollowShape

    switch (props.hollowShape) {
      case 'puzzle':
        return puzzlePath
      case 'shield':
        return shieldPath
      case 'heart':
        return heartPath
      default:
        return squarePath
    }
  }

  function drawImage() {
    const canvasEl = canvas.value
    const ctx = canvasEl?.getContext?.('2d')
    const subCanvasEl = subCanvas.value
    const subCtx = subCanvasEl?.getContext?.('2d')

    if (!imgEle || !imageLoaded || !canvasEl || !ctx || !props.image) return

    if (props.type === 'point') {
      drawImageWithTexts()
      return
    }

    if (!subCanvasEl || !subCtx || !track.value) return

    if (!memoryCanvas) {
      if (!isClient) return
      memoryCanvas = document.createElement('canvas')
    }
    memoryCanvas.width = canvasEl.width
    memoryCanvas.height = canvasEl.height

    const pathCtx = memoryCanvas.getContext('2d')

    if (!pathCtx) return

    ctx.clearRect(0, 0, canvasEl.width, canvasEl.height)
    subCtx.clearRect(0, 0, subCanvasEl.width, subCanvasEl.height)
    pathCtx.clearRect(0, 0, memoryCanvas.width, memoryCanvas.height)

    const canvasRect = canvasEl.getBoundingClientRect()

    const trackRect = track.value?.getBoundingClientRect()
    // 滑动时以轨道为准，所以需要补正 canvas 宽度和 track 宽度的差值
    const widthFix =
      ((canvasRect.width - trackRect.width) / canvasRect.width) * canvasEl.width

    const cu = currentTarget?.value
    const targetX =
      widthFix / 2 + Number(cu?.[0] || 0) * (canvasEl.width - widthFix) * 0.01
    const targetY = Number(cu?.[1] || 0) * canvasEl.height * 0.01

    const hollowProcess = getHollowProcess()

    pathCtx.beginPath()
    pathCtx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
    pathCtx.lineWidth = 4

    const [clipX, clipY, clipWidth, clipHeight] = hollowProcess({
      ctx: pathCtx,
      x: targetX,
      y: targetY,
      width: props.canvasSize?.[0],
      height: props.canvasSize?.[1],
    })

    pathCtx.stroke()
    pathCtx.clip()
    pathCtx.drawImage(imgEle, 0, 0, canvasEl.width, canvasEl.height)

    // 中心点偏移修正
    const xLeftWidth = targetX - clipX
    const translateFix = ((clipWidth * 0.5 - xLeftWidth) / clipWidth) * 100

    subCanvasEl.style.transform = `translate3d(${translateFix - 50}%, 0, 0)`
    subCanvasEl.width = clipWidth

    subCtx.drawImage(
      memoryCanvas,
      clipX,
      clipY,
      clipWidth,
      clipHeight,
      0,
      clipY,
      clipWidth,
      clipHeight
    )

    ctx.save()
    ctx.beginPath()
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)'
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
    ctx.lineWidth = 10

    hollowProcess({
      ctx,
      x: targetX,
      y: targetY,
      width: props.canvasSize[0],
      height: props.canvasSize[1],
    })

    ctx.stroke()
    ctx.fill()
    ctx.restore()
    ctx.globalCompositeOperation = 'destination-over'
    ctx.drawImage(imgEle, 0, 0, canvasEl.width, canvasEl.height)
  }

  function drawImageNextFrame() {
    nextFrameOnce(drawImage)
  }

  function stopPropagation(event: Event) {
    event.stopPropagation()
  }

  async function verifyPointers() {
    if (isLoading.value) return

    pointers.length = props.texts.length

    let result = props.remotePoint
    let customResult: unknown

    if (!props.remotePoint && canvas.value) {
      const { width, height } = canvas.value
      const fontSize = Math.max(width, height) * fontRate
      const xTolerance = (fontSize / width) * 50 + props.tolerance
      const yTolerance = (fontSize / height) * 50 + props.tolerance

      result = true

      for (let i = 0, len = pointers.length; i < len; ++i) {
        const [x, y] = pointers[i]
        const [targetX, targetY] = pointerTargets[i]

        if (
          Math.abs(x - targetX) > xTolerance ||
          Math.abs(y - targetY) > yTolerance
        ) {
          result = false
          break
        }
      }
    }

    if (typeof props.onBeforeTest === 'function') {
      nextTick(() => {
        testLoading.value = true
      })
      customResult = await (props.onBeforeTest as any)(toRaw(pointers))
      nextTick(() => {
        testLoading.value = false
      })
    }

    if (!result || customResult === false) {
      pointers.length = 0
      failFn?.()
    } else {
      successFn?.(toRaw(pointers))
    }
  }

  function handleImageClick(event: MouseEvent) {
    if (props.type !== 'point' || actionLocked.value || !canvas.value) return

    const { clientWidth: canvasWidth, clientHeight: canvasHeight } =
      canvas.value
    const { offsetX, offsetY } = event

    pointers.push([
      (offsetX / canvasWidth) * 100,
      (offsetY / canvasHeight) * 100,
    ])

    if (pointers.length >= props.texts.length) {
      nextTick(verifyPointers)
    }
  }

  function cancelPointer(index: number, event: Event) {
    event.stopPropagation()

    if (actionLocked.value || index !== pointers.length - 1) return

    pointers.pop()
  }

  async function imgReset(newImage?: string | (() => Promise<string>)) {
    if (newImage) {
      await updateImgPromise(true)
      drawImage()
    }
    pointers.length = 0
    props.type === 'point' && drawImageWithTexts()
  }

  return {
    canvas,
    subCanvas,
    actionLocked,
    handleImageClick,
    subCanvasStyle,
    pointers,
    cancelPointer,
    stopPropagation,
    isLoading,
    getImgEle: () => imgEle,
    // imagePromise,
    updateImgPromise,
    drawImageNextFrame,
    drawImage,
    imgReset,
  }
}
